import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Fa from 'react-fontawesome';
import LocaleCode from 'locale-code';
import { connect } from 'react-redux';
import { trim, first, defer } from 'lodash';
import { Field, reduxForm } from 'redux-form';
import { FormGroup, ControlLabel, FormControl, HelpBlock } from 'react-bootstrap';
import Joi from 'joi';
import DateTimeDisplay from '../../../../components/date-time-display';
import CancelOutreactForm from '../../../Feedback/components/opportunity-box/CancelOutReachForm';
import { transalteLanguageCode } from '../../../../util/helpers';

// Legacy constraints for reference:
// export const constraints = {
//   first_name: {
//     presence: true, length: { maximum: 200, minimum: 1 },
//   },
//   last_name: {
//     presence: true, length: { maximum: 200, minimum: 1 },
//   },
//   title: {
//     length: { maximum: 200 },
//   },
//   email: {
//     presence: true, email: true, length: { maximum: 200 },
//   },
//   phone: { length: { maximum: 30 } },
//   phone_ext: { length: { maximum: 10 } },
//   mobile_phone: { length: { maximum: 20 } },
//   node: { length: { maximum: 2048 } },
// };

const schema = Joi.object({
  first_name: Joi.string().min(1).max(200).required().messages({
    'string.empty': 'First name is not allowed to be empty',
    'any.required': 'First name is not allowed to be empty',
    'string.min': 'First name is not allowed to be empty',
    'string.max': 'First name must be at most 200 characters',
  }),
  last_name: Joi.string().min(1).max(200).required().messages({
    'string.empty': 'Last name is not allowed to be empty',
    'any.required': 'Last name is not allowed to be empty',
    'string.min': 'Last name is not allowed to be empty',
    'string.max': 'Last name must be at most 200 characters',
  }),
  title: Joi.string().max(200).allow('', null).messages({
    'string.max': 'Title must be at most 200 characters',
  }),
  email: Joi.string().email({ tlds: { allow: false } }).max(200).required().messages({
    'string.empty': 'Email is not allowed to be empty',
    'any.required': 'Email is not allowed to be empty',
    'string.email': 'Email must be a valid email address',
    'string.max': 'Email must be at most 200 characters',
  }),
  phone: Joi.string().max(30).allow('', null).messages({
    'string.max': 'Phone must be at most 30 characters',
  }),
  phone_ext: Joi.string().max(10).allow('', null).messages({
    'string.max': 'Phone extension must be at most 10 characters',
  }),
  mobile_phone: Joi.string().max(20).allow('', null).messages({
    'string.max': 'Mobile phone must be at most 20 characters',
  }),
  notes: Joi.string().max(2048).allow('', null).messages({
    'string.max': 'Notes must be at most 2048 characters',
  }),
}).unknown(true);

const validateContact = (contact) => {
  const { error } = schema.validate(contact, { abortEarly: false });
  return error ? error.details.reduce((acc, curr) => {
    if (!acc[curr.path[0]]) acc[curr.path[0]] = [];
    acc[curr.path[0]].push(curr.message);
    return acc;
  }, {}) : undefined;
};


class ContactEditor extends Component {
  constructor() {
    super();
    this.state = {
      terminating: false,
    };
    this.submit = this.submit.bind(this);
    this.renderField = this.renderField.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.confirmTerminate = this.confirmTerminate.bind(this);
  }


  componentWillReceiveProps(nextProps) {
    const lastId = this.props.contact ? this.props.contact._id : null;
    const { availableLanguages } = nextProps;
    const language = nextProps.language || first(availableLanguages);
    if (nextProps.contact && nextProps.contact._id !== lastId) {
      this.handleBlur();
      this.props.initialize({ language, ...nextProps.contact });
    } else if (!nextProps.contact || !nextProps.contact._id) {
      this.props.initialize({ language });
    }
  }

  submit(data) {
    // "submit on blur"
    console.log('data', data);
  }

  confirmTerminate(contact, reason) {
    return this.props.requestCancel(contact, reason);
  }

  handleBlur() {
    const { values } = this.props.formState;
    const contact = { ...values };
    contact.$invalid = validateContact(contact) !== undefined;
    this.props.onUpdated(contact);
  }

  renderField({
    input, label, componentClass, type, meta: { error }, className, options,
  }) {
    const { submitting } = this.props;
    const validationState = error ? 'error' : null;
    return (
      <FormGroup className={className} validationState={validationState}>
        <ControlLabel>{label}</ControlLabel>
        {componentClass !== 'select' &&
          <FormControl
            {...input}
            componentClass={componentClass}
            placeholder={label}
            type={type}
            disabled={submitting}
          />
        }
        {componentClass === 'select' &&
          <FormControl
            {...input}
            componentClass={componentClass}
            placeholder={label}
            type={type}
            disabled={submitting}
          >
            {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </FormControl>
        }
        {error && <HelpBlock>{error}</HelpBlock>}
      </FormGroup>
    );
  }

  renderEmpty() {
    return (
      <div className="alert alert-warning"><Fa name="arrow-left" /> Please select a contact</div>
    );
  }

  renderSubmitted() {
    const { contact } = this.props;
    const isTerminated = contact.contact_status === 'TERMINATED';
    const hasReason = isTerminated && trim(contact.terminated_reason).length > 0;
    return (
      <div className="well">
        {isTerminated &&
          <div className="text-center">
            <div>Outreach terminated on <strong><DateTimeDisplay value={contact.last_contact_status_updated} /></strong> for <strong>{contact.first_name} {contact.last_name}</strong></div>
            {hasReason &&
              <div>
                <strong>Reason:</strong> {contact.terminated_reason}
              </div>
            }
          </div>
        }
        {!isTerminated &&
          <CancelOutreactForm
            contact={contact}
            isModal={false}
            onRequestCancel={this.confirmTerminate}
          />
        }
      </div>
    );
  }

  renderLanguageSelector() {
    const { availableLanguages } = this.props;
    if (!availableLanguages || availableLanguages.length < 2) {
      return null;
    }
    const options = availableLanguages.map(language => ({
      value: language,
      label: LocaleCode.getLanguageNativeName(transalteLanguageCode(language)),
    }));
    return (
      <Field
        name="language"
        options={options}
        componentClass="select"
        component={this.renderField}
        label="Language"
        onChange={() => {
          defer(this.handleBlur);
        }}
      />
    );
  }

  render() {
    const { contact, handleSubmit, submited, canResubmit } = this.props;
    if (!contact || !contact.contact_id) {
      return this.renderEmpty();
    }
    if (submited && !canResubmit) {
      return this.renderSubmitted();
    }
    return (
      <div>
        <form
          onSubmit={handleSubmit(this.submit)}
        >
          <div className="row">
            <Field name="first_name" type="text" component={this.renderField} label="First Name" className="col-xs-6" onBlur={this.handleBlur} />
            <Field name="last_name" type="text" component={this.renderField} label="Last Name" className="col-xs-6" onBlur={this.handleBlur} />
          </div>
          <div className="row">
            <Field name="title" type="text" component={this.renderField} label="Title" className="col-xs-6" onBlur={this.handleBlur} />
            <Field name="role.role" type="text" component={this.renderField} label="Role" className="col-xs-6" onBlur={this.handleBlur} />
          </div>
          <div className="row">
            <Field name="email" type="email" component={this.renderField} label="Email" className="col-xs-6" onBlur={this.handleBlur} />
            <Field name="mobile_phone" type="text" component={this.renderField} label="Mobile" className="col-xs-6" onBlur={this.handleBlur} />
          </div>
          <div className="row">
            <Field name="phone" type="text" component={this.renderField} label="Phone" className="col-xs-6" onBlur={this.handleBlur} />
            <Field name="phone_ext" type="text" component={this.renderField} label="Extension" className="col-xs-6" onBlur={this.handleBlur} />
          </div>

          {this.renderLanguageSelector()}

          <Field name="notes" componentClass="textarea" component={this.renderField} label="Note" onBlur={this.handleBlur} />
        </form>
      </div>
    );
  }
}


ContactEditor.propTypes = {
  contact: PropTypes.shape({
    _id: PropTypes.string,
  }),
  onUpdated: PropTypes.func,
  onCancel: PropTypes.func,
  onReset: PropTypes.func,
  disabled: PropTypes.bool,

  submitting: PropTypes.bool,
  handleSubmit: PropTypes.func,
  initialize: PropTypes.func,
  reset: PropTypes.func,
  formState: PropTypes.shape({
    syncErrors: PropTypes.shape({}),
    values: PropTypes.shape({}),
    initial: PropTypes.shape({}),
  }),
  submited: PropTypes.bool,
  requestCancel: PropTypes.func,
  availableLanguages: PropTypes.arrayOf(PropTypes.string),
  language: PropTypes.string,
  canResubmit: PropTypes.bool,
};
ContactEditor.defaultProps = {
  contact: null,
  availableLanguages: [],
  onCancel: () => {},
  onUpdated: () => {},
  onReset: () => {},
  disabled: false,
  submitting: false,
  handleSubmit: () => {},
  initialize: () => {},
  reset: () => {},
  formState: {},
  submited: false,
  requestCancel: () => {},
  language: null,
  canResubmit: false,
};

const ContactEditorForm = reduxForm({
  form: 'contactEditorForm',
  validate: values => {
    const { error } = schema.validate(values, { abortEarly: false });
    if (!error) return {};
    return error.details.reduce((acc, curr) => {
      if (!acc[curr.path[0]]) acc[curr.path[0]] = [];
      acc[curr.path[0]].push(curr.message);
      return acc;
    }, {});
  },
})(ContactEditor);

const mapStateToProps = state => ({
  formState: state.form.contactEditorForm ? state.form.contactEditorForm : null,
});

export default connect(mapStateToProps)(ContactEditorForm);
